import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, RotateCcw } from 'lucide-react';

const LEVELS = [
  { target: '1/2', slices: 2, total: 4, label: 'Cut the pizza into halves!' },
  { target: '1/4', slices: 1, total: 4, label: 'Show one quarter of the pizza!' },
  { target: '3/4', slices: 3, total: 4, label: 'Show three quarters!' },
  { target: '1/3', slices: 1, total: 3, label: 'Cut into thirds - select one!' },
  { target: '2/3', slices: 2, total: 3, label: 'Show two thirds of the pizza!' },
  { target: '1/6', slices: 1, total: 6, label: 'One sixth of this pizza!' },
  { target: '5/6', slices: 5, total: 6, label: 'Show five sixths!' },
  { target: '3/8', slices: 3, total: 8, label: 'Three eighths - click the slices!' },
];

const FractionPizza = () => {
  const navigate = useNavigate();
  const [level, setLevel] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [completed, setCompleted] = useState(false);

  const current = LEVELS[level];

  const toggleSlice = (i) => {
    if (feedback) return;
    const newSet = new Set(selected);
    if (newSet.has(i)) newSet.delete(i); else newSet.add(i);
    setSelected(newSet);
  };

  const checkAnswer = () => {
    if (selected.size === current.slices) {
      setScore(s => s + 10);
      setFeedback({ correct: true, msg: `Yes! ${current.target} is correct! Great job!` });
      setTimeout(() => {
        if (level < LEVELS.length - 1) { setLevel(l => l + 1); setSelected(new Set()); setFeedback(null); }
        else setCompleted(true);
      }, 1200);
    } else {
      setFeedback({ correct: false, msg: `Not quite! Select exactly ${current.slices} out of ${current.total} slices` });
      setTimeout(() => setFeedback(null), 1500);
    }
  };

  const renderPizza = () => {
    const sliceAngle = 360 / current.total;
    return (
      <div className="relative w-72 h-72 mx-auto my-8">
        <svg viewBox="-110 -110 220 220" className="w-full h-full drop-shadow-xl">
          <circle cx="0" cy="0" r="100" fill="#FFC03D" stroke="#E6A820" strokeWidth="4" />
          {Array.from({ length: current.total }, (_, i) => {
            const startAngle = (i * sliceAngle - 90) * (Math.PI / 180);
            const endAngle = ((i + 1) * sliceAngle - 90) * (Math.PI / 180);
            const x1 = Math.cos(startAngle) * 98;
            const y1 = Math.sin(startAngle) * 98;
            const x2 = Math.cos(endAngle) * 98;
            const y2 = Math.sin(endAngle) * 98;
            const largeArc = sliceAngle > 180 ? 1 : 0;
            const isSelected = selected.has(i);
            const midAngle = ((i + 0.5) * sliceAngle - 90) * (Math.PI / 180);
            return (
              <g key={i} onClick={() => toggleSlice(i)} className="cursor-pointer">
                <path d={`M 0 0 L ${x1} ${y1} A 98 98 0 ${largeArc} 1 ${x2} ${y2} Z`}
                  fill={isSelected ? '#FF6B6B' : '#FFC03D'} stroke="#E6A820" strokeWidth="2"
                  className="hover:opacity-80 transition-all" />
                {/* Pepperoni dots */}
                <circle cx={Math.cos(midAngle) * 55} cy={Math.sin(midAngle) * 55} r="8" fill={isSelected ? '#CC4444' : '#CC6633'} />
                <circle cx={Math.cos(midAngle) * 35} cy={Math.sin(midAngle) * 35} r="5" fill={isSelected ? '#CC4444' : '#CC6633'} />
              </g>
            );
          })}
          <circle cx="0" cy="0" r="15" fill="#F5DEB3" stroke="#E6A820" strokeWidth="2" />
        </svg>
      </div>
    );
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-10 border-2 border-slate-100 shadow-2xl max-w-md w-full text-center">
          <div className="text-7xl mb-4">🍕</div>
          <h2 className="text-4xl font-black text-[#1E293B] mb-2">Pizza Master!</h2>
          <p className="text-6xl font-black text-[#FFC03D] mb-2">{score}</p>
          <p className="text-xl text-[#64748B] mb-8">points earned</p>
          <p className="text-lg text-[#64748B] mb-6">You're a fraction champion! Your pizza-cutting skills are unmatched!</p>
          <div className="space-y-3">
            <button onClick={() => { setLevel(0); setScore(0); setSelected(new Set()); setCompleted(false); setFeedback(null); }} className="w-full py-4 bg-[#FFC03D] text-white rounded-2xl font-bold text-lg border-b-4 border-[#E6A820] active:border-b-0 active:translate-y-1 transition-all">Play Again</button>
            <button onClick={() => navigate('/games')} className="w-full py-4 bg-slate-100 text-[#1E293B] rounded-2xl font-bold text-lg">Back to Brain Arena</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50" data-testid="fraction-pizza-game">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/games')} className="flex items-center gap-2 text-[#64748B] hover:text-[#1E293B] font-semibold"><ArrowLeft className="w-5 h-5" />Back</button>
          <div className="flex items-center gap-3">
            <span className="bg-[#FFC03D] text-white px-4 py-2 rounded-2xl font-bold">Level {level + 1}/{LEVELS.length}</span>
            <span className="bg-[#4D96FF] text-white px-4 py-2 rounded-2xl font-bold">{score}pts</span>
          </div>
        </div>
        <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-xl text-center">
          <h2 className="text-3xl font-black text-[#1E293B] mb-2">Show: <span className="text-[#FF6B6B]">{current.target}</span></h2>
          <p className="text-lg text-[#64748B]">{current.label}</p>
          {renderPizza()}
          <p className="text-lg font-bold text-[#1E293B] mb-4">Selected: {selected.size} / {current.total} slices</p>
          {feedback && <div className={`p-4 rounded-2xl font-bold text-lg mb-4 ${feedback.correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{feedback.msg}</div>}
          <div className="flex gap-3 justify-center">
            <button onClick={() => setSelected(new Set())} className="px-6 py-3 bg-slate-100 rounded-2xl font-bold text-[#64748B] flex items-center gap-2"><RotateCcw className="w-5 h-5" />Reset</button>
            <button onClick={checkAnswer} className="px-8 py-3 bg-[#38E54D] text-white rounded-2xl font-bold border-b-4 border-green-600 active:border-b-0 active:translate-y-1 transition-all flex items-center gap-2" data-testid="check-fraction-btn"><Check className="w-5 h-5" />Check Answer</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FractionPizza;
